import React, { useEffect, useState } from "react";
import { facilityAdmin } from "../../api";
import { PageHeader, Btn, LoadingSpinner, StatCard } from "../../components/ui";
import toast from "react-hot-toast";

const TABS = ["Daily", "Staff Performance", "Stations", "Patient Insights", "Journey Trace", "Recommendations"];

export default function ReportsPage() {
  const [tab,     setTab]    = useState(0);
  const [date,    setDate]   = useState(today());
  const [loading, setLoading]= useState(false);
  const [data,    setData]   = useState(null);

  const load = async () => {
    setLoading(true);
    setData(null);
    try {
      let res;
      if (tab === 0) res = await facilityAdmin.dailyReport({ date });
      if (tab === 1) res = await facilityAdmin.staffReport({ date_from: date, date_to: date });
      if (tab === 2) res = await facilityAdmin.stationReport({ date_from: date, date_to: date });
      if (tab === 3) res = await facilityAdmin.patientInsights({ date_from: date, date_to: date });
      if (tab === 4) return; // Journey Trace has its own search UI
      if (tab === 5) res = await facilityAdmin.aiRecommendations();
      setData(res.data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Failed to load report.`);
      console.error(`[Reports] Tab ${tab} failed:`, msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [tab, date]);

  return (
    <div style={styles.root}>
      <PageHeader title="Reports" subtitle="Queue performance analytics for your facility." />
      <div style={styles.toolbar}>
        <div style={styles.tabs}>
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)}
              style={{ ...styles.tab, ...(tab === i ? styles.tabActive : {}) }}>
              {t}
            </button>
          ))}
        </div>
        <input type="date" style={styles.datePicker}
          value={date} onChange={e => setDate(e.target.value)} />
      </div>

      {loading ? <LoadingSpinner /> : !data ? null : (
        <>
          {tab === 0 && <DailyView data={data} />}
          {tab === 1 && <StaffView data={data} />}
          {tab === 2 && <StationView data={data} />}
          {tab === 3 && data && <PatientInsightsView data={data} />}
          {tab === 4 && <JourneyTraceView />}
          {tab === 5 && data && <RecommendationsView data={data} />}
        </>
      )}
    </div>
  );
}

// ── AI Recommendations ────────────────────────────────────────────────────────
function RecommendationsView({ data }) {
  const byHour = data.raw_data?.by_hour || [];
  const byStation = data.raw_data?.by_station || [];
  const maxHourCount = Math.max(...byHour.map(r => r.count), 1);

  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Summary (last {data.period?.replace("last_", "").replace("_days", " days") || "30 days"})</div>
        <p style={{ fontSize: "14px", color: "#CBD5E1", lineHeight: 1.6, margin: 0 }}>{data.summary}</p>
        <p style={{ fontSize: "11px", color: "#475569", marginTop: "10px", marginBottom: 0 }}>
          Generated {data.generated_at ? new Date(data.generated_at).toLocaleString() : "--"} -- descriptive
          statistics from historical queue data, not a predictive model.
        </p>
      </div>

      {byHour.length > 0 && (
        <div style={V.card}>
          <div style={V.cardTitle}>Average Wait by Hour of Day</div>
          <div style={V.barChart}>
            {byHour.map(row => (
              <div key={row.hour} style={V.barCol}>
                <div style={V.barLabel}>{row.avg_minutes}</div>
                <div style={{ ...V.bar, height: `${Math.round((row.count / maxHourCount) * 100)}%`, minHeight: row.count ? "4px" : "0" }} />
                <div style={V.barHour}>{row.hour}h</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {byStation.length > 0 && (
        <div style={V.card}>
          <div style={V.cardTitle}>Busiest Stations</div>
          <table style={V.table}>
            <thead>
              <tr>
                <th style={V.th}>Station</th>
                <th style={V.th}>Tickets Served</th>
                <th style={V.th}>Avg Wait (min)</th>
              </tr>
            </thead>
            <tbody>
              {byStation.map(row => (
                <tr key={row.station} style={V.tr}>
                  <td style={V.td}>{row.station}</td>
                  <td style={V.td}>{row.tickets_served}</td>
                  <td style={V.td}>{row.avg_wait_minutes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Daily Report ──────────────────────────────────────────────────────────────
function DailyView({ data }) {
  const maxHour = Math.max(...Object.values(data.hourly_volume || {}).map(Number), 1);
  return (
    <div style={V.col}>
      <div style={V.statsRow}>
        <StatCard label="Total Tickets"  value={data.total_tickets}   color="#60A5FA" />
        <StatCard label="Completed"       value={data.completed}       color="#22C55E" />
        <StatCard label="Still in Queue"  value={data.still_in_queue}  color="#EAB308" />
        <StatCard label="Abandoned"       value={data.abandoned}       color="#EF4444" />
        <StatCard label="Expired"         value={data.expired ?? 0}    color="#F97316" />
        <StatCard label="Completion Rate" value={`${data.completion_rate ?? 0}%`} color="#A78BFA" />
        <StatCard label="Avg Wait (min)"  value={data.avg_wait_minutes ?? 0} color="#34D399" />
      </div>

      {/* Hourly volume */}
      <div style={V.card}>
        <div style={V.cardTitle}>Hourly Volume</div>
        <div style={V.barChart}>
          {Array.from({ length: 24 }, (_, h) => {
            const count = data.hourly_volume?.[h] || 0;
            return (
              <div key={h} style={V.barCol}>
                <div style={V.barLabel}>{count || ""}</div>
                <div style={{ ...V.bar, height: `${Math.round((count / maxHour) * 100)}%`, minHeight: count ? "4px" : "0" }} />
                <div style={V.barHour}>{h}h</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Station stats */}
      {(data.station_stats?.length > 0 || data.station_breakdown?.length > 0) && (
        <div style={V.card}>
          <div style={V.cardTitle}>Station Breakdown</div>
          <table style={V.table}>
            <thead><tr>
              {["Station", "Visits", "Served", "Avg Wait (min)", "Avg Service (min)"].map(h =>
                <th key={h} style={V.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {(data.station_stats || data.station_breakdown || []).map((s, i) => (
                <tr key={i} style={V.tr}>
                  <td style={V.td}>
                    <span style={{ color: "#E2E8F0" }}>{s.station_name}</span>
                    <br /><span style={{ fontSize: "11px", color: "#475569" }}>{s.zone_name}</span>
                  </td>
                  <td style={{ ...V.td, textAlign: "center" }}>{s.total_visits ?? s.served ?? 0}</td>
                  <td style={{ ...V.td, textAlign: "center", color: "#22C55E" }}>{s.total_served ?? s.served ?? 0}</td>
                  <td style={{ ...V.td, textAlign: "center", color: (s.avg_wait_minutes || s.avg_wait_min || 0) > 15 ? "#EF4444" : "#94A3B8" }}>
                    {s.avg_wait_minutes ?? s.avg_wait_min ?? 0} min
                  </td>
                  <td style={{ ...V.td, textAlign: "center" }}>
                    {s.avg_service_minutes ?? s.avg_service_min ?? 0} min
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Staff Report ──────────────────────────────────────────────────────────────
function StaffView({ data }) {
  const staffList = data.staff || data.results || [];
  const maxServed = Math.max(...staffList.map(s => s.total_served), 1);
  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Staff Performance</div>
        {staffList.length === 0 ? (
          <div style={{ color: "#475569", fontSize: "13px", padding: "16px" }}>
            No staff activity in this period. Note: staff must be assigned to journeys
            for performance data to appear.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "4px" }}>
            {staffList.map((s, i) => (
              <div key={i} style={V.staffRow}>
                <div style={V.staffRank}>#{i + 1}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "5px" }}>
                    <div>
                      <span style={{ fontSize: "14px", fontWeight: "600", color: "#E2E8F0" }}>{s.staff_name}</span>
                      <span style={{ fontSize: "11px", color: "#475569", marginLeft: "8px" }}>{s.station_name}</span>
                    </div>
                    <span style={{ fontSize: "13px", color: "#60A5FA", fontWeight: "600" }}>
                      {s.total_served} served
                    </span>
                  </div>
                  <div style={V.progressBar}>
                    <div style={{ ...V.progressFill, width: `${(s.total_served / maxServed) * 100}%` }} />
                  </div>
                  <div style={{ fontSize: "11px", color: "#475569", marginTop: "4px" }}>
                    Avg service: {s.avg_service_minutes ?? 0} min
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Station Report ────────────────────────────────────────────────────────────
function StationView({ data }) {
  const [expanded, setExpanded] = useState({});
  const toggle = (name) => setExpanded(p => ({ ...p, [name]: !p[name] }));
  const stations = data.stations || data.results || [];

  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Station Efficiency</div>
        {stations.length === 0 ? (
          <div style={{ color: "#475569", fontSize: "13px", padding: "16px" }}>
            No station activity in this period.
          </div>
        ) : (
          <table style={V.table}>
            <thead><tr>
              {["Station", "Visits", "Served", "Expired", "Avg Wait", "Avg Service", "Bottleneck?"].map(h =>
                <th key={h} style={V.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {stations.map((s, i) => (
                <React.Fragment key={i}>
                  <tr
                    style={{ ...V.tr, cursor: s.counters?.length > 0 ? "pointer" : "default" }}
                    onClick={() => s.counters?.length > 0 && toggle(s.station_name)}
                  >
                    <td style={V.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        {s.counters?.length > 0 && (
                          <span style={{ fontSize: "10px", color: "#475569" }}>
                            {expanded[s.station_name] ? "▾" : "▸"}
                          </span>
                        )}
                        <div>
                          <span style={{ color: "#E2E8F0", fontSize: "14px" }}>{s.station_name}</span>
                          <br />
                          <span style={{ fontSize: "11px", color: "#475569" }}>{s.zone_name}</span>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...V.td, textAlign: "center" }}>{s.total_visits ?? 0}</td>
                    <td style={{ ...V.td, textAlign: "center", color: "#22C55E" }}>{s.served ?? 0}</td>
                    <td style={{ ...V.td, textAlign: "center", color: s.expired_at_station > 0 ? "#F97316" : "#334155" }}>
                      {s.expired_at_station ?? 0}
                    </td>
                    <td style={{ ...V.td, textAlign: "center", color: s.avg_wait_minutes > 15 ? "#EF4444" : "#94A3B8" }}>
                      {s.avg_wait_minutes ?? 0} min
                    </td>
                    <td style={{ ...V.td, textAlign: "center", color: "#94A3B8" }}>
                      {s.avg_service_minutes ?? 0} min
                    </td>
                    <td style={{ ...V.td, textAlign: "center" }}>
                      {s.is_bottleneck ? (
                        <div>
                          <span style={{ color: "#EF4444", fontSize: "12px", fontWeight: "600" }}>⚠ Yes</span>
                          {s.bottleneck_reason && (
                            <div style={{ fontSize: "10px", color: "#EF4444", marginTop: "2px", maxWidth: "140px" }}>
                              {s.bottleneck_reason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: "#334155" }}>—</span>
                      )}
                    </td>
                  </tr>

                  {/* Counter breakdown (expandable) */}
                  {expanded[s.station_name] && s.counters?.map((c, ci) => (
                    <tr key={`${i}-${ci}`} style={{ ...V.tr, background: "rgba(37,99,235,0.04)" }}>
                      <td style={{ ...V.td, paddingLeft: "36px" }}>
                        <span style={{ fontSize: "12px", color: "#60A5FA" }}>
                          Counter {c.counter_number ?? ci + 1}: {c.counter_name}
                        </span>
                      </td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px" }}>{c.total_visits ?? 0}</td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px", color: "#22C55E" }}>{c.served ?? 0}</td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px" }}>—</td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px", color: c.avg_wait_minutes > 15 ? "#EF4444" : "#64748B" }}>
                        {c.avg_wait_minutes ?? 0} min
                      </td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px", color: "#64748B" }}>
                        {c.avg_service_minutes ?? 0} min
                      </td>
                      <td style={{ ...V.td, textAlign: "center", fontSize: "12px" }}>
                        {c.avg_wait_minutes > 15 ? (
                          <span style={{ color: "#EF4444", fontSize: "11px" }}>⚠ Slow</span>
                        ) : (
                          <span style={{ color: "#334155" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PatientInsightsView({ data }) {
  const [showFull, setShowFull] = React.useState({});
  const patients = data?.patients || [];
  const toggle = (i) => setShowFull(p => ({ ...p, [i]: !p[i] }));

  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Frequent Visitors — {patients.length} patients found</div>
        {patients.length === 0 ? (
          <div style={{ color: "#475569", fontSize: "13px", padding: "16px" }}>
            No patient data for this period.
          </div>
        ) : (
          <table style={V.table}>
            <thead><tr>
              {["Patient", "Phone", "Visits", "Last Visit", "Avg Wait (min)", "Stations Visited"].map(h =>
                <th key={h} style={V.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {patients.map((p, i) => (
                <tr key={i} style={V.tr}>
                  <td style={{ ...V.td, color: "#E2E8F0", fontWeight: "500" }}>{p.patient_name}</td>
                  <td style={V.td}>
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                      <span style={{ color: "#94A3B8", fontFamily: "monospace", fontSize: "12px" }}>
                        {showFull[i] ? p.phone : p.phone_masked}
                      </span>
                      <button onClick={() => toggle(i)}
                        style={{ background: "none", border: "none", color: "#2563EB", cursor: "pointer", fontSize: "11px", padding: "2px 6px", fontFamily: "inherit" }}>
                        {showFull[i] ? "Hide" : "Show"}
                      </button>
                    </div>
                  </td>
                  <td style={{ ...V.td, textAlign: "center" }}>
                    <span style={{ color: p.visit_count >= 5 ? "#EF4444" : p.visit_count >= 3 ? "#EAB308" : "#22C55E", fontWeight: "700" }}>
                      {p.visit_count}
                    </span>
                  </td>
                  <td style={{ ...V.td, color: "#64748B", fontSize: "12px" }}>{p.last_visit || "—"}</td>
                  <td style={{ ...V.td, textAlign: "center", color: p.avg_wait_minutes > 15 ? "#EF4444" : "#94A3B8" }}>
                    {p.avg_wait_minutes ?? 0} min
                  </td>
                  <td style={{ ...V.td, fontSize: "11px", color: "#64748B" }}>
                    {(p.stations_visited || []).join(" → ") || "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ fontSize: "11px", color: "#334155", marginTop: "14px", padding: "8px", background: "rgba(37,99,235,0.04)", borderRadius: "6px" }}>
          🔒 Phone numbers are masked by default. Click "Show" to reveal full number.
          Only facility admins can view full phone numbers.
        </div>
      </div>
    </div>
  );
}

function JourneyTraceView() {
  const [search, setSearch]         = React.useState({ type: "ticket", ticket: "", phone: "", date: today() });
  const [result, setResult]         = React.useState(null);
  const [loading, setLoading]       = React.useState(false);
  const [error,   setError]         = React.useState(null);
  const [expanded, setExpanded]     = React.useState(null);

  const handleSearch = async () => {
    setLoading(true); setError(null); setResult(null);
    try {
      const params = search.type === "ticket"
        ? { ticket_number: search.ticket, date: search.date }
        : { phone: search.phone };
      const res = await facilityAdmin.journeyTrace(params);
      setResult(res.data);
      if (res.data.traces?.length === 1) setExpanded(0);
    } catch (e) {
      setError(e.response?.data?.error || "Not found.");
    } finally { setLoading(false); }
  };

  return (
    <div style={V.col}>
      <div style={V.card}>
        <div style={V.cardTitle}>Patient Journey Trace</div>
        <div style={{ fontSize: "13px", color: "#475569", marginBottom: "16px" }}>
          Look up a patient's full journey through the facility — every station, wait time, staff member, and outcome.
        </div>

        <div style={{ display: "flex", gap: "12px", marginBottom: "14px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", gap: "0" }}>
            {["ticket", "phone"].map(t => (
              <button key={t} onClick={() => setSearch(p => ({ ...p, type: t }))}
                style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "600", fontFamily: "inherit", cursor: "pointer", border: "1px solid rgba(37,99,235,0.3)", background: search.type === t ? "rgba(37,99,235,0.2)" : "rgba(30,41,59,0.5)", color: search.type === t ? "#60A5FA" : "#64748B", borderRadius: t === "ticket" ? "8px 0 0 8px" : "0 8px 8px 0" }}>
                {t === "ticket" ? "🎫 Ticket Number" : "📱 Phone Number"}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
          {search.type === "ticket" ? (
            <>
              <input
                style={{ ...styles.datePicker, flex: 1, minWidth: "150px" }}
                placeholder="Ticket number (e.g. A-001)"
                value={search.ticket}
                onChange={e => setSearch(p => ({ ...p, ticket: e.target.value.toUpperCase() }))}
                onKeyDown={e => e.key === "Enter" && handleSearch()}
              />
              <input type="date" style={styles.datePicker}
                value={search.date}
                onChange={e => setSearch(p => ({ ...p, date: e.target.value }))} />
            </>
          ) : (
            <input
              style={{ ...styles.datePicker, flex: 1 }}
              placeholder="Phone number (e.g. +233201234567)"
              value={search.phone}
              onChange={e => setSearch(p => ({ ...p, phone: e.target.value }))}
              onKeyDown={e => e.key === "Enter" && handleSearch()}
            />
          )}
          <button onClick={handleSearch} disabled={loading}
            style={{ background: "linear-gradient(135deg,#2563EB,#1D4ED8)", border: "none", borderRadius: "8px", color: "#fff", fontSize: "13px", fontWeight: "600", padding: "8px 20px", cursor: "pointer", fontFamily: "inherit", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Searching…" : "Search →"}
          </button>
        </div>

        <div style={{ fontSize: "11px", color: "#334155", marginTop: "8px" }}>
          ⚠ Ticket numbers reset daily. Always include the date when searching by ticket number.
        </div>
      </div>

      {error && (
        <div style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "10px", padding: "14px 18px", color: "#EF4444", fontSize: "13px" }}>
          {error}
        </div>
      )}

      {result?.traces?.map((trace, ti) => (
        <div key={ti} style={V.card}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: "16px", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "22px", fontWeight: "800", color: "#60A5FA", fontVariantNumeric: "tabular-nums" }}>{trace.ticket_number}</span>
                <span style={{ fontSize: "11px", padding: "3px 8px", borderRadius: "6px", background: trace.status === "completed" ? "rgba(34,197,94,0.1)" : "rgba(234,179,8,0.1)", color: trace.status === "completed" ? "#22C55E" : "#EAB308", fontWeight: "600", textTransform: "uppercase" }}>{trace.status}</span>
                {trace.priority !== "normal" && <span style={{ color: trace.priority === "emergency" ? "#EF4444" : "#EAB308", fontSize: "14px" }}>{trace.priority === "emergency" ? "🚨" : "⚡"}</span>}
              </div>
              <div style={{ fontSize: "14px", color: "#E2E8F0", fontWeight: "500", marginTop: "4px" }}>{trace.patient_name}</div>
              <div style={{ fontSize: "12px", color: "#475569", marginTop: "2px" }}>
                {trace.date} · Issued: {trace.issued_at} · {trace.reason_for_visit}
                {trace.insurance_type && trace.insurance_type !== "—" && ` · ${trace.insurance_type}`}
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "13px", color: "#E2E8F0" }}>
                {trace.total_stations} station{trace.total_stations !== 1 ? "s" : ""} · {trace.total_minutes != null ? `${trace.total_minutes} min total` : "In progress"}
              </div>
              {trace.bottleneck_station && (
                <div style={{ fontSize: "11px", color: "#EF4444", marginTop: "3px" }}>
                  ⚠ Longest wait: {trace.bottleneck_station} ({trace.max_wait_minutes} min)
                </div>
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {trace.steps.map((step, si) => (
              <div key={si} style={{ display: "flex", gap: "12px", alignItems: "flex-start", padding: "12px 14px", borderRadius: "10px", background: step.is_bottleneck ? "rgba(239,68,68,0.06)" : "rgba(30,41,59,0.4)", border: `1px solid ${step.is_bottleneck ? "rgba(239,68,68,0.2)" : "rgba(37,99,235,0.08)"}` }}>
                <div style={{ width: "28px", height: "28px", borderRadius: "50%", background: step.status === "completed" || step.status === "transferred" ? "rgba(34,197,94,0.15)" : step.status === "skipped" ? "rgba(239,68,68,0.15)" : "rgba(37,99,235,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", flexShrink: 0 }}>
                  {step.status === "completed" || step.status === "transferred" ? "✓" : step.status === "skipped" ? "✗" : `${si + 1}`}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "14px", fontWeight: "600", color: "#E2E8F0" }}>{step.station}</span>
                    <span style={{ fontSize: "11px", color: "#475569" }}>{step.zone}</span>
                    {step.is_bottleneck && <span style={{ fontSize: "11px", color: "#EF4444", fontWeight: "700" }}>⚠ BOTTLENECK</span>}
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748B", marginTop: "4px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    <span>Arrived: <span style={{ color: "#94A3B8" }}>{step.arrived_at || "—"}</span></span>
                    <span>Called: <span style={{ color: "#94A3B8" }}>{step.called_at || "—"}</span></span>
                    <span>Done: <span style={{ color: "#94A3B8" }}>{step.completed_at || "—"}</span></span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#64748B", marginTop: "4px", display: "flex", gap: "16px", flexWrap: "wrap" }}>
                    {step.wait_minutes != null && <span>Wait: <span style={{ color: step.wait_minutes > 15 ? "#EF4444" : "#34D399", fontWeight: "600" }}>{step.wait_minutes} min</span></span>}
                    {step.service_minutes != null && <span>Service: <span style={{ color: "#60A5FA", fontWeight: "600" }}>{step.service_minutes} min</span></span>}
                    {step.staff !== "—" && <span>Staff: <span style={{ color: "#94A3B8" }}>{step.staff}</span></span>}
                    {step.counter !== "—" && <span>Counter: <span style={{ color: "#94A3B8" }}>{step.counter}</span></span>}
                    {step.outcome && <span>Outcome: <span style={{ color: "#A78BFA" }}>{step.outcome}</span></span>}
                    {step.notes && <span style={{ color: "#EF4444", fontStyle: "italic" }}>{step.notes}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function today() {
  return new Date().toISOString().split("T")[0];
}

const styles = {
  root:       { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1100px" },
  toolbar:    { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "24px", flexWrap: "wrap", gap: "12px" },
  tabs:       { display: "flex", gap: "4px" },
  tab:        { background: "none", border: "none", borderBottom: "2px solid transparent", padding: "8px 16px", fontSize: "13px", fontWeight: "500", color: "#64748B", cursor: "pointer", fontFamily: "inherit" },
  tabActive:  { color: "#60A5FA", borderBottomColor: "#2563EB" },
  datePicker: { background: "rgba(30,41,59,0.8)", border: "1px solid rgba(51,65,85,0.8)", borderRadius: "8px", padding: "8px 12px", color: "#F1F5F9", fontSize: "13px", outline: "none", fontFamily: "inherit" },
};

const V = {
  col:       { display: "flex", flexDirection: "column", gap: "16px" },
  statsRow:  { display: "flex", gap: "12px", flexWrap: "wrap" },
  card:      { background: "rgba(15,23,42,0.6)", border: "1px solid rgba(37,99,235,0.12)", borderRadius: "12px", padding: "18px 20px" },
  cardTitle: { fontSize: "13px", fontWeight: "700", color: "#475569", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "14px" },
  barChart:  { display: "flex", alignItems: "flex-end", gap: "3px", height: "100px", paddingTop: "20px" },
  barCol:    { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", height: "100%" },
  bar:       { width: "100%", background: "rgba(37,99,235,0.5)", borderRadius: "3px 3px 0 0", transition: "height 0.3s" },
  barLabel:  { fontSize: "9px", color: "#334155", minHeight: "12px" },
  barHour:   { fontSize: "9px", color: "#334155", marginTop: "2px" },
  table:     { width: "100%", borderCollapse: "collapse" },
  th:        { padding: "9px 12px", fontSize: "11px", fontWeight: "700", color: "#475569", letterSpacing: "0.06em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid rgba(37,99,235,0.1)" },
  tr:        { borderBottom: "1px solid rgba(37,99,235,0.05)" },
  td:        { padding: "11px 12px", fontSize: "13px", color: "#94A3B8", verticalAlign: "middle" },
  staffRow:  { display: "flex", gap: "12px", alignItems: "flex-start" },
  staffRank: { width: "24px", height: "24px", borderRadius: "6px", background: "rgba(37,99,235,0.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", fontWeight: "700", color: "#60A5FA", flexShrink: 0, marginTop: "2px" },
  progressBar:  { height: "5px", background: "rgba(51,65,85,0.4)", borderRadius: "3px", overflow: "hidden" },
  progressFill: { height: "100%", background: "linear-gradient(90deg,#2563EB,#60A5FA)", borderRadius: "3px", transition: "width 0.4s" },
};
